'use client';

import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '@ahafood/ui/components/avatar';
import { Card, CardContent } from '@ahafood/ui/components/card';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import type { ProcessedDeal } from '@/api/utils';
import { SnapCarousel } from '@/components/carousel/snap-carousel';
import { Link } from '@/i18n/routing';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore } from '@/store';

type Banner1Props = {
  banners: ProcessedDeal;
  isLoading: boolean;
};

export default function BannerSection({ banners, isLoading }: Banner1Props) {
  const isMobile = useIsMobile();
  const cityId = useAiFoodOrderStore.use.city_id();
  const citySlug = useAiFoodOrderStore.use.city_slug() ?? '';

  if (isLoading || !cityId) {
    return (
      <div className={cn('relative w-full py-4')}>
        <h2 className="relative z-[5] mb-4 flex items-center justify-between space-x-2 text-xl font-bold drop-shadow-sm md:text-5xl">
          <div>
            <span>🔥</span> <span>Banner</span>
          </div>
        </h2>
        <div className="flex flex-row flex-nowrap gap-4">
          {[...Array(isMobile ? 2 : 3)].map((_, index) => (
            <Skeleton
              key={index}
              className="h-[272px] w-48 flex-none rounded-xl bg-neutral-10/75 md:h-[320px] md:w-60"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!banners || banners.tags.length === 0) return null;

  return (
    <div className={cn('relative w-full py-4')}>
      <h2 className="relative z-[5] mb-4 flex items-center justify-between space-x-2 text-xl font-bold drop-shadow-sm md:text-5xl">
        <div>
          <span>🔥</span> <span>{banners.title ? banners.title : 'Deal'}</span>
        </div>
      </h2>
      <SnapCarousel dots contentClassName="-ml-4">
        {[...(banners.tags ?? [])].map((d, index) => (
          <SnapCarousel.Item
            key={index}
            className="basis-full md:basis-1/2 pl-4"
          >
            <Link href={`/${citySlug}/danh-sach-cua-hang-co-campaign-${d._id}`}>
              <Card
                style={{
                  border: 0,
                  aspectRatio: '1000/443',
                }}
                className="relative size-full overflow-hidden rounded-xl"
              >
                <CardContent className="p-0">
                  <Avatar className="w-full h-[180px] md:h-auto md:aspect-[1000/443] rounded-xl bg-white flex items-center justify-center overflow-hidden">
                    <AvatarImage
                      src={
                        d.icon_image_url ||
                        `${assetPrefix}/static/images/empty-store.webp`
                      }
                      alt={d.description_vi_vn}
                      className="size-full"
                      style={{ objectFit: isMobile ? 'contain' : 'cover' }}
                    />
                    <AvatarFallback className="size-full">
                      {d.title_vi_vn ? d.title_vi_vn.charAt(0) : 'B'}
                    </AvatarFallback>
                  </Avatar>
                </CardContent>
              </Card>
              <div className="flex w-full flex-col flex-wrap gap-2 bg-white px-4 py-3 text-xs font-medium capitalize text-neutral-50">
                <h3 className="w-full truncate text-left text-sm font-bold text-neutral-70 group-hover:line-clamp-2 group-hover:whitespace-pre-line group-hover:text-neutral-90 md:text-xl">
                  {d.title_vi_vn}
                </h3>
                {/* <div className="flex w-full flex-nowrap items-start justify-start truncate"> */}
                <span className="truncate text-left text-xs font-medium md:text-sm">
                  {d.description_vi_vn}
                </span>
                {/* </div> */}
              </div>
            </Link>
          </SnapCarousel.Item>
        ))}
      </SnapCarousel>
    </div>
  );
}
